import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  helperText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  /** If provided, renders `<option>` children automatically. Else pass children manually. */
  options?: SelectOption[];
  /** Placeholder shown as a disabled selected-empty option. */
  placeholder?: string;
}

/**
 * A native <select> with the design-system look. The full custom Select
 * (searchable, multi, async) lands later — this covers MVP needs.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    helperText,
    errorText,
    required,
    fullWidth,
    options,
    placeholder,
    id,
    className,
    children,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `select-${reactId}`;
  const helperId = helperText || errorText ? `${inputId}-helper` : undefined;
  const hasError = Boolean(errorText);

  return (
    <div className={clsx(styles.field, fullWidth && styles.fullWidth, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </label>
      )}
      <div className={clsx(styles.wrapper, hasError && styles.hasError)}>
        <select
          ref={ref}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={helperId}
          required={required}
          className={styles.select}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}
          {options
            ? options.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))
            : children}
        </select>
        <ChevronDown size={14} className={styles.chevron} aria-hidden="true" />
      </div>
      {(errorText || helperText) && (
        <p id={helperId} className={clsx(styles.helper, hasError && styles.errorText)}>
          {errorText ?? helperText}
        </p>
      )}
    </div>
  );
});
